/**
 * Sprint T-15 · Founder Security Center · API Keys tab.
 *
 * Consumes ONLY /api/security/api-keys/*. Founder-only emission.
 * Plain key is returned ONCE on create/rotate · NEVER stored client-side.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
  Copy, KeyRound, Plus, RotateCw, ShieldOff, ShieldCheck, Eye, EyeOff,
} from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge, StatusBadge } from "../advisor";
import {
  useApiKeysList, useApiKeyScopes, useApiKeyCreate, useApiKeyRevoke, useApiKeyRotate,
} from "../../hooks/useSecurity";

const fmtIso = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function PlainKeyReveal({ value, testId }) {
  const [shown, setShown] = useState(true); // shown initially because it is "once"
  if (!value) return null;
  return (
    <div
      data-testid={testId || "apikey-plain-block"}
      className="rounded border border-emerald-500/40 bg-emerald-500/5 p-3"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
        Plain key · solo se muestra esta vez
      </div>
      <div className="mt-2 flex items-center gap-2">
        <code
          data-testid="apikey-plain-text"
          className="flex-1 break-all rounded bg-[var(--ag-surface-2)] px-2 py-1.5 font-mono text-xs"
        >
          {shown ? value : "•".repeat(40)}
        </code>
        <button
          type="button"
          data-testid="apikey-plain-toggle"
          onClick={() => setShown((s) => !s)}
          className="rounded border border-[var(--ag-border)] px-2 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          aria-label={shown ? "Ocultar" : "Mostrar"}
        >
          {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
        <button
          type="button"
          data-testid="apikey-plain-copy"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              toast.success("API key copiada al portapapeles");
            } catch {
              toast.error("No se pudo copiar");
            }
          }}
          className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
        >
          <Copy className="h-3 w-3" /> Copiar
        </button>
      </div>
      <p className="mt-2 text-[10px] text-[var(--ag-text-dim)]">
        Guárdala en tu gestor de secretos. AutoGlow no la almacena · no podrás recuperarla.
      </p>
    </div>
  );
}

function CreateKeyForm({ scopes, onCreated }) {
  const create = useApiKeyCreate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleScope = (s) => {
    setSelectedScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || selectedScopes.length === 0) return;
    setSubmitting(true);
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        scopes: selectedScopes,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      onCreated?.(res);
      setName("");
      setDescription("");
      setSelectedScopes([]);
      setExpiresAt("");
      toast.success("API key creada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creando API key");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="apikey-create-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Nombre *</label>
          <input
            data-testid="apikey-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Webhook Resend"
            maxLength={120}
            required
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Expira (opcional)</label>
          <input
            data-testid="apikey-expires-input"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">Descripción (opcional)</label>
        <input
          data-testid="apikey-desc-input"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          placeholder="Para qué se usa esta key"
          className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">
          Scopes * (mínimo 1)
        </label>
        <div className="flex flex-wrap gap-2" data-testid="apikey-scopes-list">
          {(scopes || []).map((s) => {
            const active = selectedScopes.includes(s);
            return (
              <button
                key={s}
                type="button"
                data-testid={`apikey-scope-${s.replace(/[:.]/g, "-")}`}
                onClick={() => toggleScope(s)}
                className={`rounded border px-2 py-1 font-mono text-[10px] ${
                  active
                    ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-500"
                    : "border-[var(--ag-border)] bg-[var(--ag-surface)] text-[var(--ag-text-dim)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
      <button
        data-testid="apikey-create-btn"
        type="submit"
        disabled={submitting || !name.trim() || selectedScopes.length === 0}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        <Plus className="h-3 w-3" />
        {submitting ? "Creando…" : "Crear API key"}
      </button>
    </form>
  );
}

export default function ApiKeysTab() {
  const list = useApiKeysList();
  const scopes = useApiKeyScopes();
  const revoke = useApiKeyRevoke();
  const rotate = useApiKeyRotate();

  const [plainKey, setPlainKey] = useState(null);  // shown after create/rotate

  const onRevoke = async (id) => {
    if (!window.confirm("¿Revocar esta API key? La acción es irreversible.")) return;
    try {
      await revoke.mutateAsync(id);
      toast.success("API key revocada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error revocando key");
    }
  };

  const onRotate = async (id) => {
    if (!window.confirm("¿Rotar esta API key? Se generará una nueva y la antigua quedará revocada.")) return;
    try {
      const res = await rotate.mutateAsync(id);
      if (res?.key) setPlainKey(res.key);
      toast.success("API key rotada · guarda la nueva clave");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error rotando key");
    }
  };

  const items = list.data?.items || [];

  return (
    <section className="space-y-4" data-testid="security-apikeys-tab">
      <WidgetCard
        title="Crear nueva API key"
        description="Las claves son founder-only · prefijo ag_live_ · 160 bits · sólo plaintext en respuesta de creación."
        citations={["/api/security/api-keys/", "/api/security/api-keys/scopes"]}
        testId="widget-apikey-create"
      >
        {scopes.isLoading && <LoadingState />}
        {scopes.error && <EmptyState message="No se pudieron cargar los scopes." />}
        {scopes.data && (
          <CreateKeyForm scopes={scopes.data.scopes} onCreated={(res) => setPlainKey(res?.key || null)} />
        )}
      </WidgetCard>

      {plainKey && (
        <WidgetCard
          title="API key generada"
          description="Cópiala AHORA. No volverá a mostrarse."
          citations={["create_api_key · plaintext output"]}
          testId="widget-apikey-plain"
        >
          <PlainKeyReveal value={plainKey} />
          <button
            type="button"
            data-testid="apikey-plain-dismiss"
            onClick={() => setPlainKey(null)}
            className="mt-2 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
          >
            La he guardado · ocultar
          </button>
        </WidgetCard>
      )}

      <WidgetCard
        title={`API Keys (${items.length})`}
        description="Listado read-only · hashes nunca expuestos · audit log en cada acción."
        citations={["/api/security/api-keys/", "audit.api_key.*"]}
        testId="widget-apikey-list"
      >
        {list.isLoading && <LoadingState />}
        {list.error && <EmptyState message="No se pudo cargar el listado." />}
        {list.data && items.length === 0 && (
          <EmptyState
            title="Sin API keys."
            message="Crea una key arriba para integrar webhooks o partners B2B."
            testId="apikey-empty"
          />
        )}
        {list.data && items.length > 0 && (
          <div className="-mx-1 overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="apikey-table">
              <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                <tr>
                  <th className="px-2 py-2 text-left">Nombre</th>
                  <th className="px-2 py-2 text-left">Prefix</th>
                  <th className="px-2 py-2 text-left">Scopes</th>
                  <th className="px-2 py-2 text-left">Estado</th>
                  <th className="px-2 py-2 text-left">Creada</th>
                  <th className="px-2 py-2 text-left">Último uso</th>
                  <th className="px-2 py-2 text-left">Expira</th>
                  <th className="px-2 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <tr
                    key={k.id}
                    className="border-b border-[var(--ag-border)]/60"
                    data-testid={`apikey-row-${k.id}`}
                  >
                    <td className="px-2 py-2 align-top">
                      <div className="font-medium">{k.name}</div>
                      {k.description && (
                        <div className="text-[10px] text-[var(--ag-text-dim)]">{k.description}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <code className="rounded bg-[var(--ag-surface-2)] px-1 py-0.5 font-mono text-[10px]">
                        {k.prefix}…
                      </code>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <div className="flex flex-wrap gap-1">
                        {(k.scopes || []).map((s) => (
                          <span
                            key={s}
                            className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 font-mono text-[9px]"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top">
                      {k.revoked ? (
                        <StatusBadge status="critico" label="revocada" testId={`apikey-status-${k.id}`} />
                      ) : k.active ? (
                        <StatusBadge status="excelente" label="activa" testId={`apikey-status-${k.id}`} />
                      ) : (
                        <StatusBadge status="medio" label="inactiva" testId={`apikey-status-${k.id}`} />
                      )}
                    </td>
                    <td className="px-2 py-2 align-top text-[10px] text-[var(--ag-text-dim)]">
                      {fmtIso(k.created_at)}
                    </td>
                    <td className="px-2 py-2 align-top text-[10px] text-[var(--ag-text-dim)]">
                      {k.last_used ? fmtIso(k.last_used) : "—"}
                    </td>
                    <td className="px-2 py-2 align-top text-[10px] text-[var(--ag-text-dim)]">
                      {k.expires_at ? fmtIso(k.expires_at) : "Sin expiración"}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <div className="flex flex-wrap justify-end gap-1">
                        {!k.revoked && (
                          <>
                            <button
                              data-testid={`apikey-rotate-${k.id}`}
                              type="button"
                              onClick={() => onRotate(k.id)}
                              disabled={rotate.isPending}
                              className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)] disabled:opacity-50"
                            >
                              <RotateCw className="h-3 w-3" /> Rotar
                            </button>
                            <button
                              data-testid={`apikey-revoke-${k.id}`}
                              type="button"
                              onClick={() => onRevoke(k.id)}
                              disabled={revoke.isPending}
                              className="inline-flex items-center gap-1 rounded border border-red-500/40 px-2 py-0.5 text-[10px] text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                            >
                              <ShieldOff className="h-3 w-3" /> Revocar
                            </button>
                          </>
                        )}
                        {k.revoked && (
                          <span className="text-[10px] text-[var(--ag-text-dim)]">
                            revocada {fmtIso(k.revoked_at)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["T-14 backend", "SHA-256 hashed at rest", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
